/**
 * PasswordChangeForm - nova senha + confirmação, chamando updatePassword.
 *
 * Compartilhado por ResetPasswordPage (link de recuperação) e
 * ChangePasswordPage (troca obrigatória após senha temporária do suporte).
 * Com `strongPolicy`, aplica a mesma política do signup (a API também exige
 * quando a troca é obrigatória) e mostra o checklist das regras.
 */

import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Lock, KeyRound, Check, X } from 'lucide-react';
import { isPasswordValid, validatePassword, type PasswordValidation } from '@/lib/passwordPolicy';
import { cn } from '@/lib/utils';

interface PasswordChangeFormProps {
  /** Exige 8+ caracteres, maiúscula, minúscula e especial (com checklist). */
  strongPolicy: boolean;
  onSuccess: () => void;
}

const RULES: Array<{ key: keyof PasswordValidation; label: string }> = [
  { key: 'minLength', label: 'changePassword.rules.minLength' },
  { key: 'uppercase', label: 'changePassword.rules.uppercase' },
  { key: 'lowercase', label: 'changePassword.rules.lowercase' },
  { key: 'special', label: 'changePassword.rules.special' },
];

const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({ strongPolicy, onSuccess }) => {
  const { t } = useTranslation();
  const { updatePassword } = useAuth();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validation = validatePassword(password);
  const minLength = strongPolicy ? 8 : 6;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!password.trim()) {
      setError(t('reset.informPassword'));
      return;
    }

    if (strongPolicy && !isPasswordValid(validation)) {
      setError(t('changePassword.weakPassword'));
      return;
    }

    if (!strongPolicy && password.length < 6) {
      setError(t('reset.passwordMinLength'));
      return;
    }

    if (password !== confirmPassword) {
      setError(t('reset.passwordsDontMatch'));
      return;
    }

    setLoading(true);

    try {
      const { error } = await updatePassword(password);
      if (error) {
        if (error.message === 'auth.errors.samePassword' || error.message.includes('same password')) {
          setError(t('reset.samePassword'));
        } else {
          setError(error.message.startsWith('auth.') ? t(error.message) : error.message);
        }
      } else {
        onSuccess();
      }
    } catch {
      setError(t('reset.unexpectedError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    // noValidate: as mensagens (traduzidas) vêm do handleSubmit, não do tooltip nativo do navegador.
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      {/* New Password */}
      <div>
        <Label htmlFor="password" className="text-sm font-medium">
          {t('reset.newPassword')}
        </Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={strongPolicy ? t('changePassword.placeholderPassword') : t('reset.placeholderPassword')}
            className="pl-10"
            disabled={loading}
            required
            minLength={minLength}
            autoComplete="new-password"
            autoFocus
          />
        </div>
      </div>

      {strongPolicy && (
        <ul className="space-y-1" aria-label={t('changePassword.rulesTitle')}>
          {RULES.map((rule) => {
            const ok = validation[rule.key];
            return (
              <li
                key={rule.key}
                className={cn('flex items-center gap-2 text-xs', ok ? 'text-green-600 dark:text-green-500' : 'text-muted-foreground')}
              >
                {ok ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                {t(rule.label)}
              </li>
            );
          })}
        </ul>
      )}

      {/* Confirm Password */}
      <div>
        <Label htmlFor="confirmPassword" className="text-sm font-medium">
          {t('reset.confirmPassword')}
        </Label>
        <div className="relative mt-1">
          <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('reset.confirmPlaceholder')}
            className="pl-10"
            disabled={loading}
            required
            minLength={minLength}
            autoComplete="new-password"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3">
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {/* Submit Button */}
      <Button type="submit" className="w-full h-11" disabled={loading}>
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            {t('reset.updating')}
          </>
        ) : (
          <>
            <KeyRound className="h-4 w-4 mr-2" />
            {t('reset.submit')}
          </>
        )}
      </Button>
    </form>
  );
};

export default PasswordChangeForm;
